import { useState } from "react";
import type { FocusEvent } from "react";

import { TriangleAlertIcon } from "lucide-react";

import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Message, MessageHeader } from "simplydo/react/ui/message";

import { FieldProps } from "./FieldProps";

const getNumericFieldValue = (value: FieldProps["fieldValue"]): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value !== "" && value !== "-") {
    return Number.parseFloat(value);
  }
  return Number.NaN;
};

const NumericalText = ({
  field,
  fieldValue = "",
  updateIdea,
  isEditing,
}: FieldProps) => {
  const minValue = field.minValue ?? undefined;
  const maxValue = field.maxValue ?? undefined;
  const [validEntry, setValidEntry] = useState(true);
  // Keeps partial input such as "1." that parses to the stored number
  const [text, setText] = useState("");
  const parsedText = text === "" || text === "-" ? text : parseFloat(text);
  const inputValue = Object.is(parsedText, fieldValue)
    ? text
    : String(fieldValue ?? "");
  if (!isEditing) {
    return <span>{String(fieldValue ?? "")} </span>;
  }
  const isNumber = (value: unknown) =>
    !fieldValue || !Number.isNaN(parseFloat(String(value)));

  let message: string | null = null;
  if (fieldValue && fieldValue !== "-" && !isNumber(fieldValue)) {
    message = `Enter a number between ${minValue} and ${maxValue}.`;
  } else if (
    minValue !== undefined &&
    getNumericFieldValue(fieldValue) < minValue
  ) {
    message = `Enter a number greater than ${minValue}.`;
  } else if (
    maxValue !== undefined &&
    getNumericFieldValue(fieldValue) > maxValue
  ) {
    message = `Enter a number less than ${maxValue}.`;
  }

  return (
    <div>
      {message && (
        <Message color="red" size="small">
          <Icon icon={TriangleAlertIcon} />
          <MessageHeader>{message}</MessageHeader>
        </Message>
      )}
      <Input
        fluid
        value={inputValue}
        onChange={(_e, { value }) => {
          //Allow for minus sign to be written first
          if (value === "-" || value === "") {
            setText(value);
            updateIdea(value, ["templated", field.id]);
            return;
          }
          //Prevent letters and other invalid inputs
          if (!/^[-]?\d+(\.\d*)?$/.test(value)) {
            return;
          }

          setText(value);
          const num = parseFloat(value);
          const isValidMin =
            minValue === undefined || !isNumber(minValue) || num >= minValue;
          const isValidMax =
            maxValue === undefined || !isNumber(maxValue) || num <= maxValue;
          if (isValidMin && isValidMax) {
            setValidEntry(true);
          }
          updateIdea(num, ["templated", field.id]);
        }}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          const num = parseFloat(e.target.value);
          const isValidMin =
            minValue === undefined || !isNumber(minValue) || num >= minValue;
          const isValidMax =
            maxValue === undefined || !isNumber(maxValue) || num <= maxValue;
          setValidEntry(isNumber(num) && isValidMin && isValidMax);
        }}
        readOnly={!isEditing}
        type="text"
        error={!validEntry}
      />
    </div>
  );
};

export default NumericalText;
